import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    ActivityIndicator,
    Dimensions,
    FlatList,
    Keyboard,
    Modal,
    NativeSyntheticEvent,
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TextInputKeyPressEventData,
    TouchableOpacity,
    View
} from "react-native";
import {type Href, router, useFocusEffect, useLocalSearchParams} from "expo-router";
import {useSafeAreaInsets} from "react-native-safe-area-context";
import {Bell, BellOff, Send, Users} from "lucide-react-native";
import {useTranslation} from "react-i18next";
import {clearActiveChannel, setActiveChannel} from "@/lib/active-channel";
import {pb} from "@/lib/pocketbase";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {HeaderText} from "@/components/common/HeaderText";
import {useChatChannel, useStartDm} from "@/data/chat-hooks";
import {CHAT_MESSAGE_NOTIFICATION_TYPE} from "@/data/notification-api";
import {useResolvedNotificationSetting, useSetNotificationSetting} from "@/data/notification-hooks";

export default function ChatChannelScreen() {
    const {orgId, channelId, name} = useLocalSearchParams<{orgId: string; channelId: string; name?: string}>();
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const {messages, loading, hasMore, error, members, presence, typingUserIds, sendMessage, loadMore, startTyping, stopTyping, retry} =
        useChatChannel(channelId);
    const {mutateAsync: startDm} = useStartDm(orgId);
    // The RESOLVED value for this channel, which is what will actually govern delivery — so with
    // chat muted type-wide this reads as muted here too, and switching it on stores the exception
    // ("mute chat, except this conversation") the settings model exists to express.
    const channelNotificationsOn = useResolvedNotificationSetting(CHAT_MESSAGE_NOTIFICATION_TYPE, channelId);
    const {mutate: setNotificationSetting, isPending: muteWritePending} = useSetNotificationSetting();
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [kbHeight, setKbHeight] = useState(0);
    const [rosterOpen, setRosterOpen] = useState(false);
    const insets = useSafeAreaInsets();
    // throttle typing notifications: emit once, re-arm while keystrokes keep coming
    const typingActiveRef = useRef(false);
    const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Tell the foreground notification handler which conversation is on screen, so a push about
    // THIS channel does not drop a banner over the message it is announcing.
    //
    // Focus rather than mount: pushing the roster's DM screen on top leaves this one mounted but
    // no longer visible, and a register that only cleared on unmount would keep swallowing this
    // channel's notifications from behind the screen the user actually moved to. `useFocusEffect`
    // covers both — it releases on blur and on unmount.
    useFocusEffect(
        useCallback(() => {
            setActiveChannel(channelId);
            return () => clearActiveChannel(channelId);
        }, [channelId])
    );

    // Lift the composer to exactly the keyboard's top while open, and back to 0 on dismiss.
    // KeyboardAvoidingView left residual padding under Android edge-to-edge; and the event's
    // `endCoordinates.height` under-reports by the nav-bar inset there, so we derive the lift from
    // the keyboard's top (`screenY`) against the full screen height instead.
    useEffect(() => {
        const showEvt = Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
        const hideEvt = Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";
        const show = Keyboard.addListener(showEvt, (e) => {
            const screenH = Dimensions.get("screen").height;
            setKbHeight(Math.max(0, screenH - e.endCoordinates.screenY));
        });
        const hide = Keyboard.addListener(hideEvt, () => setKbHeight(0));
        return () => {
            show.remove();
            hide.remove();
        };
    }, []);

    const myId = pb.authStore.record?.id;
    const inverted = useMemo(() => [...messages].reverse(), [messages]);

    const nameFor = (userId: string) => members.find((m) => m.user_id === userId)?.user?.display_name ?? userId;
    const roster = useMemo(() => members.filter((m) => m.user_id !== myId), [members, myId]);

    // The roster is the sole entry point for DMs: tapping a member opens (or reuses) a DM with
    // them. The roster belongs to one of this org's group channels, so the DM is org-correct.
    const openDm = async (otherUserId: string, displayName: string) => {
        try {
            const {channelId: dmChannelId} = await startDm(otherUserId);
            router.push({
                pathname: "/organization/[orgId]/chat/[channelId]",
                params: {orgId, channelId: dmChannelId, name: displayName}
            } as unknown as Href);
        } catch {
            // From a shared-group roster the backend always allows the DM; swallow transient failures.
        }
    };
    const typingText = useMemo(() => {
        const names = typingUserIds.map(nameFor);
        if (names.length === 0) return null;
        if (names.length === 1) return t("chat.typing", {name: names[0]});
        if (names.length === 2) return t("chat.typingTwo", {a: names[0], b: names[1]});
        return t("chat.typingMany");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typingUserIds, members, t]);

    const notifyTyping = () => {
        // Emit at most once per 2.5s window. We deliberately don't push the timer out on
        // each keystroke: letting the window lapse lets the next keystroke re-emit before
        // the SDK's ~3s server-side auto-stop fires, so a continuous typist keeps showing.
        if (typingActiveRef.current) return;
        typingActiveRef.current = true;
        startTyping();
        typingTimerRef.current = setTimeout(() => {
            typingActiveRef.current = false;
            typingTimerRef.current = null;
        }, 2500);
    };
    const endTyping = () => {
        if (typingTimerRef.current) {
            clearTimeout(typingTimerRef.current);
            typingTimerRef.current = null;
        }
        if (typingActiveRef.current) {
            typingActiveRef.current = false;
            stopTyping();
        }
    };

    useEffect(() => () => {
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    }, []);

    const onSend = async () => {
        const body = text.trim();
        if (!body || sending) return;
        setSending(true);
        setText("");
        endTyping();
        try {
            await sendMessage(body);
        } catch {
            setText(body); // restore so the user can retry
        } finally {
            setSending(false);
        }
    };

    // Mobile: the soft-keyboard return key arrives as a trailing "\n". Treat that as send
    // (no Shift on mobile, so every Enter sends). onSend() reads the pre-newline `text` and clears it.
    const onChangeText = (val: string) => {
        if (Platform.OS !== "web" && val.endsWith("\n")) {
            void onSend();
            return;
        }
        setText(val);
        if (val.trim().length > 0) notifyTyping();
        else endTyping();
    };

    // Web/desktop: plain Enter sends, Shift+Enter inserts a newline.
    const onKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        const ev = e.nativeEvent as TextInputKeyPressEventData & {shiftKey?: boolean};
        if (Platform.OS === "web" && ev.key === "Enter" && !ev.shiftKey) {
            (e as unknown as {preventDefault?: () => void}).preventDefault?.();
            void onSend();
        }
    };

    return (
        <View style={{flex: 1, backgroundColor: theme.colors.background, paddingBottom: kbHeight}}>
            <HeaderText
                title={name ?? t("chat.title")}
                onBackPress={() => router.back()}
                rightActions={
                    // Nothing at all until the resolve answers: an icon that guesses would tell a
                    // user who muted this channel that it is unmuted, and the guess is invisible.
                    channelNotificationsOn === undefined ? undefined : (
                        <TouchableOpacity
                            // Same padding the header's own icon buttons carry, so the tap target
                            // is a button rather than a 22px glyph and the two line up.
                            style={styles.headerAction}
                            activeOpacity={0.7}
                            disabled={muteWritePending}
                            onPress={() =>
                                setNotificationSetting({
                                    type: CHAT_MESSAGE_NOTIFICATION_TYPE,
                                    discriminator: channelId,
                                    enabled: !channelNotificationsOn
                                })
                            }
                            accessibilityRole="button"
                            // The label is the ACTION the tap performs; the value carries the
                            // current state, which the icon alone conveys only to people who can
                            // see it.
                            accessibilityLabel={channelNotificationsOn ? t("chat.mute") : t("chat.unmute")}
                            accessibilityValue={channelNotificationsOn ? undefined : {text: t("chat.muted")}}
                        >
                            {channelNotificationsOn ? (
                                <Bell color={theme.colors.primary} size={22} />
                            ) : (
                                <BellOff color={theme.colors.textSecondary} size={22} />
                            )}
                        </TouchableOpacity>
                    )
                }
                rightIcon={
                    roster.length > 0 ? <Users color={theme.colors.primary} size={22} accessibilityLabel={t("chat.members")} /> : undefined
                }
                onRightPress={roster.length > 0 ? () => setRosterOpen(true) : undefined}
            />

            {loading && (
                <View style={styles.centered}>
                    <ActivityIndicator color={theme.colors.primary} />
                </View>
            )}

            {error && !loading && (
                <View style={styles.centered}>
                    {/* The underlying message stays on screen on purpose: when this fails in the
                        field, the exact cause is what makes it diagnosable without a repro. */}
                    <Text style={[styles.errorText, {color: theme.colors.error}]}>
                        {t("chat.loadError")}: {error.message}
                    </Text>
                    <TouchableOpacity
                        onPress={retry}
                        style={[styles.retryButton, {borderColor: theme.colors.primary}]}
                        accessibilityRole="button"
                    >
                        <Text style={{color: theme.colors.primary}}>{t("errors.actions.tryAgain")}</Text>
                    </TouchableOpacity>
                </View>
            )}

            {!loading && !error && (
                <FlatList
                    data={inverted}
                    inverted
                    keyExtractor={(m) => m.id}
                    contentContainerStyle={{padding: 12}}
                    onEndReached={() => {
                        if (hasMore) void loadMore();
                    }}
                    onEndReachedThreshold={0.3}
                    renderItem={({item}) => {
                        const mine = item.sender_id === myId;
                        return (
                            <View style={[styles.bubbleRow, {justifyContent: mine ? "flex-end" : "flex-start"}]}>
                                <View
                                    style={[
                                        styles.bubble,
                                        {
                                            backgroundColor: mine ? theme.colors.primary : theme.colors.surface,
                                            borderColor: theme.colors.border
                                        }
                                    ]}
                                >
                                    {!mine && (
                                        <Text style={[styles.sender, {color: theme.colors.textSecondary}]}>
                                            {item.sender?.display_name ?? item.sender_id}
                                        </Text>
                                    )}
                                    <Text style={{color: mine ? "#ffffff" : theme.colors.text}}>{item.body}</Text>
                                </View>
                            </View>
                        );
                    }}
                />
            )}

            {typingText && (
                <Text style={[styles.typing, {color: theme.colors.textSecondary}]} numberOfLines={1}>
                    {typingText}
                </Text>
            )}

            <View
                style={[
                    styles.composer,
                    {
                        borderColor: theme.colors.border,
                        backgroundColor: theme.colors.surface,
                        paddingBottom: kbHeight > 0 ? 8 : 8 + insets.bottom
                    }
                ]}
            >
                <TextInput
                    value={text}
                    onChangeText={onChangeText}
                    onKeyPress={onKeyPress}
                    placeholder={t("chat.messagePlaceholder")}
                    placeholderTextColor={theme.colors.textSecondary}
                    style={[styles.input, {color: theme.colors.text}]}
                    multiline
                    blurOnSubmit={false}
                />
                <Pressable
                    onPress={onSend}
                    disabled={sending || text.trim().length === 0}
                    style={[styles.sendBtn, {backgroundColor: theme.colors.primary, opacity: text.trim() ? 1 : 0.5}]}
                >
                    <Send color="#ffffff" size={18} />
                </Pressable>
            </View>

            <Modal visible={rosterOpen} transparent animationType="fade" onRequestClose={() => setRosterOpen(false)}>
                <Pressable style={styles.backdrop} onPress={() => setRosterOpen(false)}>
                    <Pressable
                        style={[styles.panel, {top: insets.top + 52, backgroundColor: theme.colors.surface, borderColor: theme.colors.border}]}
                        onPress={() => {}}
                    >
                        <Text style={[styles.panelTitle, {color: theme.colors.textSecondary}]}>{t("chat.members")}</Text>
                        <ScrollView style={styles.panelList}>
                            {roster.map((m) => {
                                const online = presence[m.user_id] === "online";
                                const displayName = m.user?.display_name ?? m.user_id;
                                return (
                                    <Pressable
                                        key={m.user_id}
                                        onPress={() => {
                                            setRosterOpen(false);
                                            void openDm(m.user_id, displayName);
                                        }}
                                        style={styles.memberRow}
                                        accessibilityRole="button"
                                        accessibilityLabel={t("chat.startDm", {name: displayName})}
                                    >
                                        <View
                                            style={[styles.presenceDot, {backgroundColor: online ? "#22c55e" : theme.colors.border}]}
                                        />
                                        <Text
                                            style={[styles.rosterName, {color: online ? theme.colors.text : theme.colors.textSecondary}]}
                                            numberOfLines={1}
                                        >
                                            {displayName}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                        </ScrollView>
                    </Pressable>
                </Pressable>
            </Modal>
        </View>
    );
}

const styles = StyleSheet.create({
    centered: {flex: 1, alignItems: "center", justifyContent: "center"},
    errorText: {textAlign: "center", paddingHorizontal: 24},
    retryButton: {marginTop: 16, paddingVertical: 8, paddingHorizontal: 20, borderWidth: 1, borderRadius: 8},
    headerAction: {padding: 4, justifyContent: "center", alignItems: "center"},
    backdrop: {flex: 1},
    panel: {
        position: "absolute",
        right: 12,
        minWidth: 200,
        maxWidth: 280,
        borderRadius: 12,
        borderWidth: StyleSheet.hairlineWidth,
        paddingVertical: 6,
        shadowColor: "#000",
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.18,
        shadowRadius: 8,
        elevation: 6
    },
    panelTitle: {fontSize: 11, fontWeight: "700", letterSpacing: 0.5, textTransform: "uppercase", paddingHorizontal: 12, paddingVertical: 6},
    panelList: {maxHeight: 320},
    memberRow: {flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 12, paddingVertical: 10},
    presenceDot: {width: 8, height: 8, borderRadius: 4},
    rosterName: {fontSize: 14, fontWeight: "500", flexShrink: 1},
    typing: {paddingHorizontal: 14, paddingBottom: 4, fontSize: 12, fontStyle: "italic"},
    bubbleRow: {flexDirection: "row", marginBottom: 8},
    bubble: {maxWidth: "80%", borderRadius: 14, borderWidth: StyleSheet.hairlineWidth, paddingHorizontal: 12, paddingVertical: 8},
    sender: {fontSize: 12, marginBottom: 2, fontWeight: "600"},
    composer: {flexDirection: "row", alignItems: "flex-end", gap: 8, padding: 8, borderTopWidth: StyleSheet.hairlineWidth},
    input: {flex: 1, maxHeight: 120, paddingHorizontal: 12, paddingVertical: 8, fontSize: 16},
    sendBtn: {width: 40, height: 40, borderRadius: 20, alignItems: "center", justifyContent: "center"}
});
